import { ButtonTab, ButtonTabs } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useParams } from "react-router";
import styled from "styled-components";
import { LocalContactViewProvider } from "../../contactViews/local/LocalContactViewProvider";
import { contactUpdateInquiryService } from "../../shared/api/contactUpdateInquiryService";
import { CompletedUpdateInquiryContactList } from "./contactLists/CompletedUpdateInquiryContactList";
import { OpenDeletionInquiryContactList } from "./contactLists/OpenDeletionInquiryContactList";
import { OpenUpdateInquiryContactList } from "./contactLists/OpenUpdateInquiryContactList";
import { PendingFeedbackContactList } from "./contactLists/PendingFeedbackContactList";
import { ContactUpdateInquiryBaseData } from "./ContactUpdateInquiryBaseData";
import { ContactUpdateInquiryPerformance } from "./ContactUpdateInquiryPerformance";

const Wrapper = styled.div`
	display: grid;
	grid-template-columns: 1fr;
	gap: ${({ theme }) => theme.spacing(4)};
	align-items: start;

	${({ theme }) => theme.breakpoints.largeDesktop} {
		grid-template-columns: minmax(380px, 1fr) 3fr;
		height: 100%;
	}
`;

const Sidebar = styled.div`
	display: grid;
	gap: ${({ theme }) => theme.spacing(4)};
`;

const Content = styled.div`
	display: grid;
	grid-template-rows: min-content 1fr;
	gap: ${({ theme }) => theme.spacing(4)};
	align-items: start;
	height: 100%;
	position: relative;
	overflow: auto;
`;

const StyledButtonTabs = styled(ButtonTabs)`
	> * {
		padding: ${({ theme }) => theme.spacing(1)} 0;
	}
`;

export const ContactUpdateInquiryDetails: FC = () => {
	const { t } = useTranslation();
	const { contactUpdateInquiryId = "" } = useParams<"contactUpdateInquiryId">();
	const { data } = useQuery({
		queryKey: contactUpdateInquiryService.detailsKey(contactUpdateInquiryId),
		queryFn: () => contactUpdateInquiryService.details(contactUpdateInquiryId).then((r) => r.data.data),
	});

	if (!data) return null;

	return (
		<Wrapper>
			<Sidebar>
				<ContactUpdateInquiryBaseData contactUpdateInquiry={data} />
				<ContactUpdateInquiryPerformance contactUpdateInquiry={data} />
			</Sidebar>
			<Content>
				<StyledButtonTabs>
					<ButtonTab to="../open-inquiries">
						{t("Update inquiries")} ({data.updateListCount})
					</ButtonTab>
					<ButtonTab to="../deletion-inquiries">
						{t("Deletion inquiries")} ({data.deleteListCount})
					</ButtonTab>
					<ButtonTab to="../pending-inquiries">
						{t("Pending feedback")} ({data.pendingListCount})
					</ButtonTab>
					<ButtonTab to="../completed-inquiries">
						{t("Completed feedback")} ({data.completedListCount})
					</ButtonTab>
				</StyledButtonTabs>
				<LocalContactViewProvider contactViewKey="contactUpdateInquiryList">
					<Routes>
						<Route index element={<Navigate replace to="open-inquiries" />} />
						<Route
							path="open-inquiries"
							element={<OpenUpdateInquiryContactList contactUpdateInquiryId={contactUpdateInquiryId} />}
						/>
						<Route
							path="pending-inquiries"
							element={<PendingFeedbackContactList contactUpdateInquiryId={contactUpdateInquiryId} />}
						/>
						<Route
							path="deletion-inquiries"
							element={<OpenDeletionInquiryContactList contactUpdateInquiryId={contactUpdateInquiryId} />}
						/>
						<Route
							path="completed-inquiries"
							element={<CompletedUpdateInquiryContactList contactUpdateInquiryId={contactUpdateInquiryId} />}
						/>
					</Routes>
				</LocalContactViewProvider>
			</Content>
		</Wrapper>
	);
};
